import { useState } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.includes('/uploads/')) {
    const uploadsPath = url.substring(url.indexOf('/uploads'));
    return `${BACKEND_URL}/api${uploadsPath}`;
  }
  return url;
};

const emptyKitten = {
  name: '',
  breed: 'Scottish Fold',
  age: '',
  gender: 'Male',
  color: '',
  price: '',
  status: 'Available',
  images: [],
  personality: '',
  health_details: '',
  vaccination_status: '',
};

// Helper to get status badge styles
const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'Available':
      return 'bg-green-100 text-green-800';
    case 'Reserved':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function AdminKittens({ kittens, onRefresh }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingKitten, setEditingKitten] = useState(null);
  const [kittenForm, setKittenForm] = useState(emptyKitten);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedData = {
      ...kittenForm,
      price: parseFloat(kittenForm.price),
      images: kittenForm.images.filter((img) => img.trim() !== ''),
    };

    try {
      if (editingKitten) {
        await adminApi.put(`/kittens/${editingKitten.id}`, formattedData);
        toast.success('Kitten updated successfully!');
      } else {
        await adminApi.post(`/kittens`, formattedData);
        toast.success('Kitten added successfully!');
      }
      onRefresh();
      setIsDialogOpen(false);
      setKittenForm(emptyKitten);
      setEditingKitten(null);
    } catch {
      toast.error('Failed to save kitten');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this kitten?')) return;

    try {
      await adminApi.delete(`/kittens/${id}`);
      toast.success('Kitten deleted successfully!');
      onRefresh();
    } catch {
      toast.error('Failed to delete kitten');
    }
  };

  const handleEdit = (kitten) => {
    setEditingKitten(kitten);
    setKittenForm({
      ...kitten,
      images: kitten.images || [],
    });
    setIsDialogOpen(true);
  };

  const removeImageField = (index) => {
    const newImages = kittenForm.images.filter((_, i) => i !== index);
    setKittenForm({ ...kittenForm, images: newImages });
  };

  // Handle file upload - Using Cloudinary for permanent URLs
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }

        toast.info(`Uploading ${file.name}...`);
        
        // Upload to Cloudinary
        const result = await uploadToCloudinary(file, 'kittens');
        uploadedUrls.push(result.url);
      }

      setKittenForm({
        ...kittenForm,
        images: [...kittenForm.images, ...uploadedUrls],
      });

      toast.success(`${uploadedUrls.length} image(s) uploaded successfully!`);
    } catch {
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(Array.from(e.target.files));
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-stone-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-medium text-stone-800">Manage Kittens</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-kitten-button"
              onClick={() => {
                setEditingKitten(null);
                setKittenForm(emptyKitten);
              }}
              className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Kitten
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingKitten ? 'Edit Kitten' : 'Add New Kitten'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Name *</label>
                  <Input
                    value={kittenForm.name}
                    onChange={(e) => setKittenForm({ ...kittenForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Breed *</label>
                  <Select
                    value={kittenForm.breed}
                    onValueChange={(value) => setKittenForm({ ...kittenForm, breed: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Scottish Fold">Scottish Fold</SelectItem>
                      <SelectItem value="Scottish Straight">Scottish Straight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Age *</label>
                  <Input
                    value={kittenForm.age}
                    onChange={(e) => setKittenForm({ ...kittenForm, age: e.target.value })}
                    placeholder="e.g., 8 weeks"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Gender *</label>
                  <Select
                    value={kittenForm.gender}
                    onValueChange={(value) => setKittenForm({ ...kittenForm, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Color *</label>
                  <Input
                    value={kittenForm.color}
                    onChange={(e) => setKittenForm({ ...kittenForm, color: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Price *</label>
                  <Input
                    type="number"
                    value={kittenForm.price}
                    onChange={(e) => setKittenForm({ ...kittenForm, price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">Status *</label>
                  <Select
                    value={kittenForm.status}
                    onValueChange={(value) => setKittenForm({ ...kittenForm, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Available">Available</SelectItem>
                      <SelectItem value="Reserved">Reserved</SelectItem>
                      <SelectItem value="Sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">Upload Images</label>
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-[#E85D75] bg-[#E85D75]/5'
                      : 'border-stone-300 hover:border-[#E85D75]/50'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="text-4xl">📤</div>
                    <div>
                      <p className="text-sm font-medium text-stone-700">Drag & drop images here</p>
                      <p className="text-xs text-stone-500 mt-1">or click to browse</p>
                    </div>
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      accept="image/*"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('file-upload').click()}
                      variant="outline"
                      disabled={uploadingImages}
                      className="mt-2"
                    >
                      {uploadingImages ? 'Uploading...' : '📁 Browse Files'}
                    </Button>
                    <p className="text-xs text-stone-400">JPG, PNG, WEBP (Max 5MB each)</p>
                  </div>
                </div>

                {kittenForm.images.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-stone-600 mb-3">
                      Uploaded Images ({kittenForm.images.length})
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {kittenForm.images.map((image, index) => (
                        <div
                          key={`kitten-image-preview-${index}-${image.slice(-10)}`}
                          className="relative group aspect-square rounded-lg overflow-hidden border border-stone-200"
                        >
                          <img
                            src={normalizeImageUrl(image)}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeImageField(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <details className="mt-4">
                  <summary className="text-xs text-stone-500 cursor-pointer hover:text-stone-700">
                    Or add image URL manually
                  </summary>
                  <div className="mt-2 space-y-2">
                    <Input
                      placeholder="https://example.com/image.jpg"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const url = e.target.value.trim();
                          if (url) {
                            setKittenForm({
                              ...kittenForm,
                              images: [...kittenForm.images, url],
                            });
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <p className="text-xs text-stone-400">Press Enter to add</p>
                  </div>
                </details>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">Personality</label>
                <Textarea
                  value={kittenForm.personality}
                  onChange={(e) => setKittenForm({ ...kittenForm, personality: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">Health Details</label>
                <Textarea
                  value={kittenForm.health_details}
                  onChange={(e) => setKittenForm({ ...kittenForm, health_details: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">Vaccination Status</label>
                <Textarea
                  value={kittenForm.vaccination_status}
                  onChange={(e) => setKittenForm({ ...kittenForm, vaccination_status: e.target.value })}
                  rows={2}
                />
              </div>

              <Button
                type="submit"
                data-testid="save-kitten-button"
                className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
              >
                {editingKitten ? 'Update Kitten' : 'Add Kitten'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700">Name</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700">Breed</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700">Age</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700">Price</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700">Status</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {kittens.map((kitten) => (
              <tr
                key={kitten.id}
                data-testid={`kitten-row-${kitten.id}`}
                className="border-b border-stone-100 hover:bg-stone-50"
              >
                <td className="py-3 px-4 text-stone-700">{kitten.name}</td>
                <td className="py-3 px-4 text-stone-600 text-sm">{kitten.breed}</td>
                <td className="py-3 px-4 text-stone-600 text-sm">{kitten.age}</td>
                <td className="py-3 px-4 text-stone-700 font-semibold">${kitten.price}</td>
                <td className="py-3 px-4">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs ${getStatusBadgeClass(kitten.status)}`}
                  >
                    {kitten.status}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <Button onClick={() => handleEdit(kitten)} variant="ghost" size="sm" className="mr-2">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(kitten.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {kittens.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            No kittens added yet. Click "Add Kitten" to get started.
          </div>
        )}
      </div>
    </div>
  );
}
